import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, TextArea } from '@/components';

import { FinancialHighlights, Project } from '@/features/projects/types';

import { nullableStringSetter } from '@/helpers';

export type FinancialHighlightsSubmitFields = {
  details: string;
};

export type FinancialHighlightsFormFields = {
  details: string;
};

export type FinancialHighlightsFormProps = {
  readonly id: string;
  readonly project: Project;
  readonly onSubmit: (data: FinancialHighlightsSubmitFields) => void;
};

export function FinancialHighlightsForm({ id, project, onSubmit }: FinancialHighlightsFormProps) {
  const { t } = useTranslation(['projects']);
  const item = project.financial_highlights?.[0] ?? ({} as Partial<FinancialHighlights>);

  const options = useMemo(
    () => ({
      defaultValues: {
        details: item.details ?? null,
      },
    }),
    [item]
  );

  const onSubmitFinancialHighlightsForm = useCallback(
    (data: FinancialHighlightsFormFields) => {
      const payload = {
        details: data.details,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<FinancialHighlightsFormFields>
      id={id}
      onSubmit={(data) => onSubmitFinancialHighlightsForm(data)}
      options={options}
    >
      {({ formState, register }) => (
        <div className="space-y-6">
          <Field
            id="details"
            label={t('Details')}
            error={formState.errors['details']}
            delay={0.6}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="details"
              color={formState.errors['details'] ? 'error' : 'primary'}
              registration={register('details', { setValueAs: nullableStringSetter })}
              className="min-h-32"
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
